import React, { useState, useEffect } from 'react';
import { Power, Ban, X } from 'lucide-react';
import {
  getWorkers,
  terminateSession,
  enableMonitoring,
  disableMonitoring,
} from '../services/api';
import { PageHeader } from '../components/ui/page-header';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select } from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';

function ControlPage() {
  const [workers, setWorkers] = useState([]);
  const [selectedWorker, setSelectedWorker] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    loadWorkers();
  }, []);

  const loadWorkers = async () => {
    try {
      const data = await getWorkers();
      setWorkers(data.data || []);
    } catch (err) {
      console.error('Failed to load workers:', err);
    }
  };

  const handleTerminateSession = async () => {
    if (!selectedWorker) return;
    try {
      setLoading(true);
      await terminateSession(parseInt(selectedWorker));
      setMessage({ type: 'success', text: 'Session termination command sent' });
    } catch (err) {
      setMessage({ type: 'destructive', text: 'Failed to terminate session' });
    } finally {
      setLoading(false);
    }
  };

  const handleEnableMonitoring = async (types) => {
    if (!selectedWorker) return;
    try {
      setLoading(true);
      await enableMonitoring(parseInt(selectedWorker), types);
      setMessage({ type: 'success', text: 'Monitoring enabled' });
    } catch (err) {
      setMessage({ type: 'destructive', text: 'Failed to enable monitoring' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto flex max-w-[1400px] flex-col gap-6">
      <PageHeader
        title="Endpoint Control"
        description="Execute remote commands on monitored endpoints."
      />

      <div className="space-y-1.5">
        <label className="text-sm font-medium text-foreground">Select Worker</label>
        <Select
          value={selectedWorker}
          onChange={(e) => setSelectedWorker(e.target.value)}
          className="max-w-md"
        >
          <option value="">-- Select a worker --</option>
          {workers.map((worker) => (
            <option key={worker.id} value={worker.id}>
              {worker.worker_name} ({worker.status})
            </option>
          ))}
        </Select>
      </div>

      {message && (
        <Alert variant={message.type === 'success' ? 'success' : 'destructive'}>
          <div className="flex items-start justify-between gap-3">
            <AlertDescription>{message.text}</AlertDescription>
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => setMessage(null)}
              className="-mt-1 -mr-1"
            >
              <X />
            </Button>
          </div>
        </Alert>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Power className="h-4 w-4" />
              Session Control
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Button
              variant="destructive"
              className="w-full"
              onClick={handleTerminateSession}
              disabled={!selectedWorker || loading}
            >
              Terminate Session
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Ban className="h-4 w-4" />
              Monitoring Control
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-2">
            <Button
              variant="outline"
              className="w-full"
              onClick={() => handleEnableMonitoring(['clipboard', 'usb', 'network'])}
              disabled={!selectedWorker || loading}
            >
              Enable All Monitoring
            </Button>
            <Button
              variant="outline"
              className="w-full"
              onClick={() =>
                disableMonitoring(parseInt(selectedWorker), ['clipboard', 'usb', 'network'])
              }
              disabled={!selectedWorker || loading}
            >
              Disable All Monitoring
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default ControlPage;
